// In-run HUD, chip-rail layout (mobile-first redesign, variant A): a 3px XP
// strip pinned to the very top edge IS the Lumensurge meter. Below it, a
// top-left rail of 24px vitals chips (HP / hull+shield / flash charges, plus
// a conditional badge row for serenity / RAM / event chips) and a top-right
// rail (level / wave / timer / kills, with the CALL / SHOP buttons and the
// enemies / score chips on a second row). The bottom-left captain module +
// bottom stats strip are unchanged. Pixel-framed chips + Silkscreen; layout
// lives in theme.ts.
//
// UI language rule (keep consistent): CHUNKED resources get condensed counts
// (heart ×N, hull points, flash ×N). CONTINUOUS meters get 2px underline
// fills inside their chips (shield, serenity) — a resource never gets both.
// Vitals chips are color-coded by a 2px accent border: HP red, hull cyan,
// flash yellow, level gold.

import type { WorldState } from '../sim/state.js';
import type { Vec2 } from '../types.js';
import { RAM_SPEED_THRESHOLD } from '../sim/combat.js';
import { getWaveInfo } from '../sim/director.js';
import { getSerenityMods } from '../sim/serenity.js';
import { thresholdForLevel } from '../sim/upgrade-system.js';
import { wrappedDelta } from '../sim/collision.js';
import { SKILL_IDS, SKILL_MAX_PIPS, SKILL_META, type SkillId } from '../captain/skills.js';
import {
  colors,
  ensureUiStyles,
  icon,
  pixelGlyph,
  pixelShadow,
  SKULL_GLYPH,
  type RunScore,
} from './theme.js';

const ICON_ENEMIES = 'bug'; // live foes pressing in

// HP is shown as a condensed heart count: one heart per 20 max HP, filled
// while that 20-HP chunk remains (100 HP = ×5). Max-HP upgrades grow the /M
// suffix. (Was a pip row; the chip rail keeps only the count.)
const HP_PER_HEART = 20;

// Hull reads as the blue damage buffer (matches the shield blue) and is
// spent before red HP.
const HULL_COLOR = '#4db8ff';

// Wormhole bonus-game indicator color: the mythical-rarity purple, matching
// the boon card frames in the upgrade overlay.
const BONUS_COLOR = '#c86bff';

// Space storm indicator color: the ice blue of the firebolt visuals.
const STORM_COLOR = '#9fefff';

/**
 * Generated pixel-art HUD icon (public/assets/icons/hud/*.png — heart,
 * shield, skull, bolt, star), referenced as a relative URL the same way the
 * captain atlas is ('captain/atlas.png' in captain/compositor.ts). Rendered
 * crisp at 16px via the .la-hud-icon class (image-rendering: pixelated) —
 * 16px files point-sampled /4 from the 64px style-guide master.
 */
function hudIcon(name: string): HTMLImageElement {
  const img = document.createElement('img');
  img.className = 'la-hud-icon';
  img.src = `assets/icons/hud/${name}.png`;
  img.alt = '';
  img.setAttribute('aria-hidden', 'true');
  return img;
}

/** One rail chip: the framed box element plus its text span. */
type HudChip = { el: HTMLElement; text: HTMLElement };

export class Hud {
  private readonly root: HTMLElement;
  private readonly xpFill: HTMLElement;
  private readonly xpLevelText: HTMLElement;
  private readonly hpText: HTMLElement;
  private readonly hpTotalText: HTMLElement;
  private readonly hullChip: HudChip;
  private readonly hullText: HTMLElement;
  private readonly hullUnder: HTMLElement;
  private readonly flashChip: HudChip;
  private readonly flashText: HTMLElement;
  private readonly ramChip: HudChip;
  private readonly serenityChip: HudChip;
  private readonly serenityUnder: HTMLElement;
  private readonly serenityText: HTMLElement;
  private readonly bonusChip: HudChip;
  private readonly stormChip: HudChip;
  private readonly waveChip: HudChip;
  private readonly waveChipText: HTMLElement;
  private readonly enemyChipText: HTMLElement;
  private readonly scoreChipText: HTMLElement;
  private readonly timerText: HTMLElement;
  private readonly fpsText: HTMLElement;
  private readonly killChipText: HTMLElement;
  private readonly killText: HTMLElement;
  private readonly killRow: HTMLElement;
  private readonly captainRow: HTMLElement;
  private skillsGridEl: HTMLElement | null = null;
  // Mobile bottom stats strip (design v3): multiplier · enemies · score on
  // the left, kills on the right. Desktop keeps these in the top bars, so
  // the strip is hidden at 768px+ (stylesheet).
  private stripMultText: HTMLElement | null = null;
  private stripEnemyText: HTMLElement | null = null;
  private stripScoreText: HTMLElement | null = null;
  private stripKillText: HTMLElement | null = null;
  private speechEl: HTMLElement | null = null;
  private readonly captainCanvas: HTMLCanvasElement;
  private readonly captainNameEl: HTMLElement;
  private readonly captainPips: Record<SkillId, HTMLElement[]> = {
    gunnery: [], piloting: [], engineering: [], deadeye: [], salvage: [], daring: [],
  };
  private readonly radarEl: HTMLElement;
  private readonly radarArrows: HTMLElement[] = [];
  private readonly specialArrows: HTMLElement[] = [];
  /** Off-screen specials to point at (wormhole, exit portal, black holes); game.ts pushes. */
  private specialTargets: { x: number; y: number; kind: 'wormhole' | 'blackhole' | 'exit' }[] = [];
  private readonly callWaveBtn: HTMLButtonElement;
  private readonly callWaveLabel: HTMLElement;
  private onCallWave: (() => void) | null = null;
  private readonly shopBtn: HTMLButtonElement;
  private onShop: (() => void) | null = null;
  private readonly bannerEl: HTMLElement;
  /** Last showBanner time (performance.now ms) — the occlusion dim only runs
   * while a banner is actually live (~2 s hold+fade). */
  private bannerShownAt = -Infinity;
  private readonly metaPanel: HTMLElement;
  private bonusActive = false;
  private stormActive = false;
  private frameCount = 0;
  private fpsTimer = 0;
  private lastFps = 0;
  private showFps = false;

  constructor(container: HTMLElement) {
    ensureUiStyles();

    this.root = document.createElement('div');
    this.root.className = 'la-hud';
    container.appendChild(this.root);

    const top = document.createElement('div');
    top.className = 'la-hud-top';
    this.root.appendChild(top);
    const bottom = document.createElement('div');
    bottom.className = 'la-hud-bottom';
    this.root.appendChild(bottom);

    // Top edge: the 3px full-width XP strip IS the Lumensurge meter (mote
    // progress toward the next tier). Below it, two chip rails replace the
    // old corner panels.
    const xpStrip = document.createElement('div');
    xpStrip.className = 'la-xp-strip';
    this.xpFill = document.createElement('div');
    this.xpFill.className = 'la-xp-fill';
    xpStrip.appendChild(this.xpFill);
    top.appendChild(xpStrip);

    // Top-left rail, row 1 (vitals): HP condensed to a heart ×N count, the
    // hull+shield chip, flash charges. Row 2 holds the conditional badges
    // (serenity / RAM / wormhole / storm), so the corner never grows past
    // two rows at 390px.
    const railTL = document.createElement('div');
    railTL.className = 'la-rail la-rail-tl';
    top.appendChild(railTL);
    const vitalsRow = document.createElement('div');
    vitalsRow.className = 'la-rail-row';
    railTL.appendChild(vitalsRow);

    const hpChip = this.makeChip(hudIcon('heart'));
    // la-hp-row: capture/debug probes hook this class to find the vitals.
    hpChip.el.classList.add('la-hp-row', 'la-chip-hp');
    hpChip.el.style.color = colors.hp;
    this.hpText = hpChip.text;
    // Dim "/M" suffix keeps the max-HP read while hearts are missing.
    this.hpTotalText = document.createElement('span');
    this.hpTotalText.className = 'la-muted';
    this.hpTotalText.style.display = 'none';
    hpChip.el.appendChild(this.hpTotalText);
    vitalsRow.appendChild(hpChip.el);

    // Hull + shield share one chip (both are the blue damage buffer): the
    // text is the hull point count while a hull exists, otherwise the shield
    // pool; the shield meter is the 2px underline fill. Hidden until an
    // upgrade grants max_hull or max_shield.
    this.hullChip = this.makeChip(hudIcon('shield'));
    this.hullChip.el.classList.add('la-chip-hull');
    this.hullChip.el.style.color = HULL_COLOR;
    this.hullChip.el.style.display = 'none';
    this.hullText = this.hullChip.text;
    this.hullUnder = this.makeUnderline(this.hullChip.el);
    vitalsRow.appendChild(this.hullChip.el);

    // Flash charges: one bolt + live count, dimmed while the dash is spent.
    this.flashChip = this.makeChip(hudIcon('bolt'));
    this.flashChip.el.classList.add('la-chip-flash');
    this.flashChip.el.style.color = colors.accent;
    this.flashText = this.flashChip.text;
    vitalsRow.appendChild(this.flashChip.el);

    const badgeRow = document.createElement('div');
    badgeRow.className = 'la-rail-row';
    railTL.appendChild(badgeRow);

    // Serenity stance chip: fills while the ship is slow; the underline
    // reads ice → gold at tier 3, the tier label is desktop-width only.
    this.serenityChip = this.makeChip(icon('moon-solid', 12));
    this.serenityChip.el.style.color = '#9fefff';
    this.serenityChip.el.style.display = 'none';
    this.serenityText = this.serenityChip.text;
    this.serenityText.className = 'la-chip-label la-small la-muted';
    this.serenityUnder = this.makeUnderline(this.serenityChip.el);
    badgeRow.appendChild(this.serenityChip.el);

    // RAM chip: lit while the player is at ram speed, where contact damages
    // the enemy and the recoil hits the hull instead of shields.
    this.ramChip = this.makeChip(hudIcon('bolt'));
    this.ramChip.el.style.color = colors.accent;
    this.ramChip.el.style.display = 'none';
    this.ramChip.text.className = 'la-small la-accent';
    this.ramChip.text.textContent = 'RAM';
    badgeRow.appendChild(this.ramChip.el);

    // WORMHOLE chip: lit while the bonus game runs (game.ts calls
    // setBonusActive). Icon-only on phones; the label earns its width at
    // 768px+ (la-chip-label).
    this.bonusChip = this.makeChip(hudIcon('star'));
    this.bonusChip.el.style.color = BONUS_COLOR;
    this.bonusChip.el.style.display = 'none';
    this.bonusChip.text.className = 'la-chip-label la-small';
    this.bonusChip.text.textContent = 'WORMHOLE';
    this.bonusChip.text.style.letterSpacing = '2px';
    badgeRow.appendChild(this.bonusChip.el);

    // SPACE STORM chip: same pattern as WORMHOLE (setSpaceStormActive).
    this.stormChip = this.makeChip(icon('cloud-fog', 12));
    this.stormChip.el.style.color = STORM_COLOR;
    this.stormChip.el.style.display = 'none';
    this.stormChip.text.className = 'la-chip-label la-small';
    this.stormChip.text.textContent = 'SPACE STORM';
    this.stormChip.text.style.letterSpacing = '2px';
    badgeRow.appendChild(this.stormChip.el);

    // Top-right rail, row 1: level chip (star + ×tier; the XP strip above
    // carries the progress), wave chip (Wn / Bn on boss waves), run timer
    // (desktop-width only via la-chip-opt), kills. Row 2: the enemies /
    // score chips (480px+; the bottom strip carries them on phones) and the
    // intermission action buttons. la-wave-row stays as a probe hook for the
    // capture scripts.
    const railTR = document.createElement('div');
    railTR.className = 'la-rail la-rail-tr la-wave-row';
    top.appendChild(railTR);
    const statsRow = document.createElement('div');
    statsRow.className = 'la-rail-row';
    railTR.appendChild(statsRow);

    const lvlChip = this.makeChip(hudIcon('star'));
    lvlChip.el.classList.add('la-chip-level');
    lvlChip.el.style.color = colors.accent;
    this.xpLevelText = lvlChip.text;
    statsRow.appendChild(lvlChip.el);

    this.waveChip = this.makeChip(null);
    this.waveChipText = this.waveChip.text;
    statsRow.appendChild(this.waveChip.el);

    const timerChip = this.makeChip(null);
    timerChip.el.classList.add('la-chip-opt');
    this.timerText = timerChip.text;
    statsRow.appendChild(timerChip.el);

    const killChip = this.makeChip(hudIcon('skull'));
    this.killChipText = killChip.text;
    statsRow.appendChild(killChip.el);

    const actionRow = document.createElement('div');
    actionRow.className = 'la-rail-row';
    railTR.appendChild(actionRow);

    const enemyChip = this.makeChip(icon(ICON_ENEMIES, 12));
    enemyChip.el.classList.add('la-chip-opt');
    this.enemyChipText = enemyChip.text;
    actionRow.appendChild(enemyChip.el);

    const scoreChip = this.makeChip(icon('trophy', 12));
    scoreChip.el.classList.add('la-chip-opt');
    this.scoreChipText = scoreChip.text;
    actionRow.appendChild(scoreChip.el);

    // Bravery call: skips the intermission for bonus points. Shown only while
    // a wave is callable; the label carries the live bonus (hud.update).
    this.callWaveBtn = this.makeWaveButton('startups');
    this.callWaveLabel = document.createElement('span');
    this.callWaveBtn.appendChild(this.callWaveLabel);
    this.callWaveBtn.addEventListener('click', () => this.onCallWave?.());
    actionRow.appendChild(this.callWaveBtn);
    // Supply drop: the intermission shop (score wallet). Same button style as
    // CALL; shown only during intermission while the run is live (hud.update).
    this.shopBtn = this.makeWaveButton('shop');
    const shopLabel = document.createElement('span');
    shopLabel.textContent = 'SHOP';
    this.shopBtn.appendChild(shopLabel);
    this.shopBtn.addEventListener('click', () => this.onShop?.());
    actionRow.appendChild(this.shopBtn);

    // Bottom-left: FPS (dev overlay) + the corner module (captain first).
    this.metaPanel = this.makePanel(bottom, 'la-pos-bl');
    this.fpsText = document.createElement('div');
    this.fpsText.className = 'la-row la-fps';
    this.fpsText.style.display = 'none';
    this.metaPanel.appendChild(this.fpsText);

    // Pilot seat FIRST (design v2): the captain's framed portrait with a name
    // plate beneath, and SIX rank cells in one row — each cell a small framed
    // icon box with three vertical pip bars under it. The hosted speech
    // bubble lands above with a tail pointing at the portrait; while the
    // captain speaks it REPLACES the rest of the module (see update()).
    this.captainRow = this.makeRow();
    this.captainRow.classList.add('la-captain-seat-row');
    this.captainRow.style.alignItems = 'flex-start';
    this.captainRow.style.gap = '10px';
    const portraitCol = document.createElement('div');
    portraitCol.style.display = 'flex';
    portraitCol.style.flexDirection = 'column';
    portraitCol.style.alignItems = 'center';
    portraitCol.style.gap = '3px';
    portraitCol.style.flex = 'none';
    this.captainCanvas = document.createElement('canvas');
    this.captainCanvas.width = 48;
    this.captainCanvas.height = 48;
    const cc = this.captainCanvas.style;
    cc.width = '48px';
    cc.height = '48px';
    cc.imageRendering = 'pixelated';
    cc.boxShadow = pixelShadow(colors.accent, 2);
    cc.background = colors.barTrack;
    portraitCol.appendChild(this.captainCanvas);
    this.captainNameEl = document.createElement('div');
    this.captainNameEl.className = 'la-small';
    this.captainNameEl.style.color = colors.muted;
    this.captainNameEl.style.maxWidth = '76px';
    this.captainNameEl.style.textAlign = 'center';
    this.captainNameEl.style.overflow = 'hidden';
    this.captainNameEl.style.textOverflow = 'ellipsis';
    this.captainNameEl.style.whiteSpace = 'nowrap';
    portraitCol.appendChild(this.captainNameEl);
    this.captainRow.appendChild(portraitCol);
    const skillsGrid = document.createElement('div');
    skillsGrid.style.display = 'grid';
    // Compact 3×2 (two rows of three) — a single row of six made the corner
    // module sprawl across the bottom of the screen.
    skillsGrid.style.gridTemplateColumns = 'repeat(3, auto)';
    skillsGrid.style.gap = '4px';
    skillsGrid.style.alignSelf = 'center';
    this.skillsGridEl = skillsGrid;
    for (const id of SKILL_IDS) {
      const meta = SKILL_META[id];
      const cell = document.createElement('div');
      cell.style.display = 'flex';
      cell.style.flexDirection = 'column';
      cell.style.alignItems = 'center';
      // Rank cell: ONE framed box holding both the skill icon and its three
      // level pips (icon on top, pip bars along the bottom inside edge).
      const iconBox = document.createElement('div');
      iconBox.style.width = '22px';
      iconBox.style.height = '28px';
      iconBox.style.display = 'flex';
      iconBox.style.flexDirection = 'column';
      iconBox.style.alignItems = 'center';
      iconBox.style.justifyContent = 'center';
      iconBox.style.gap = '2px';
      iconBox.style.boxSizing = 'border-box';
      iconBox.style.padding = '3px 0';
      iconBox.style.background = colors.barTrack;
      iconBox.style.boxShadow = pixelShadow(colors.panelBorder, 2);
      iconBox.style.color = colors.accent;
      iconBox.appendChild(icon(meta.icon, 11));
      const pipRow = document.createElement('div');
      pipRow.style.display = 'inline-flex';
      pipRow.style.gap = '2px';
      const pips: HTMLElement[] = [];
      for (let i = 0; i < SKILL_MAX_PIPS; i++) {
        const pip = document.createElement('span');
        pip.style.width = '3px';
        pip.style.height = '6px';
        pip.style.background = 'rgba(120,140,170,0.25)';
        pipRow.appendChild(pip);
        pips.push(pip);
      }
      this.captainPips[id] = pips;
      iconBox.appendChild(pipRow);
      cell.appendChild(iconBox);
      skillsGrid.appendChild(cell);
    }
    this.captainRow.appendChild(skillsGrid);
    this.metaPanel.appendChild(this.captainRow);

    this.killRow = this.makeRow();
    this.killRow.classList.add('la-kill-row');
    this.killRow.appendChild(pixelGlyph(SKULL_GLYPH));
    this.killText = document.createElement('span');
    this.killRow.appendChild(this.killText);
    const killLabel = document.createElement('span');
    killLabel.className = 'la-small la-muted';
    killLabel.textContent = 'KILLS';
    this.killRow.appendChild(killLabel);
    this.metaPanel.appendChild(this.killRow);

    // Mobile bottom stats strip (design v3): full-width row pinned above
    // the pilot seat — [bolt ×tier] [bug enemies] [trophy score] left,
    // [skull kills] right. Hidden at 768px+ (desktop shows these up top).
    const strip = document.createElement('div');
    strip.className = 'la-stats-strip';
    const stripLeft = document.createElement('div');
    stripLeft.className = 'la-stats-strip-group';
    const stripItem = (glyph: HTMLElement): HTMLElement => {
      const item = document.createElement('span');
      item.className = 'la-stats-strip-item';
      item.appendChild(glyph);
      const text = document.createElement('span');
      item.appendChild(text);
      stripLeft.appendChild(item);
      return text;
    };
    this.stripMultText = stripItem(icon('bolt'));
    this.stripEnemyText = stripItem(icon(ICON_ENEMIES));
    this.stripScoreText = stripItem(icon('trophy'));
    strip.appendChild(stripLeft);
    const stripKills = document.createElement('span');
    stripKills.className = 'la-stats-strip-item';
    stripKills.appendChild(pixelGlyph(SKULL_GLYPH));
    this.stripKillText = document.createElement('span');
    stripKills.appendChild(this.stripKillText);
    const killsLabel = document.createElement('span');
    killsLabel.className = 'la-small la-muted';
    killsLabel.textContent = 'KILLS';
    stripKills.appendChild(killsLabel);
    strip.appendChild(stripKills);
    this.metaPanel.insertBefore(strip, this.captainRow);

    // Radar: edge-of-screen arrows toward off-view enemies (only when the
    // field looks empty but the arena isn't — "where do I find them?").
    // Hollow ice outline + pulse so they read as radar UI, never as tiny
    // red enemies (a filled red triangle mimics a driftfin silhouette).
    this.radarEl = document.createElement('div');
    const rs = this.radarEl.style;
    rs.position = 'fixed';
    rs.inset = '0';
    rs.pointerEvents = 'none';
    rs.zIndex = '25';
    for (let i = 0; i < 3; i++) {
      const arrow = document.createElement('div');
      arrow.textContent = '▲';
      const as = arrow.style as CSSStyleDeclaration & { webkitTextStroke?: string };
      as.position = 'absolute';
      as.display = 'none';
      as.color = 'transparent';
      // Small edge chevron (~40% smaller than the original 14px/1.5px): the
      // filled boss arrow read as a glitched oversized sprite at desktop.
      as.webkitTextStroke = '1px #9fefff';
      as.fontSize = '8px';
      as.textShadow = '0 0 6px rgba(159, 239, 255, 0.6)';
      this.radarArrows.push(arrow);
      this.radarEl.appendChild(arrow);
    }
    // Special-object indicators (wormhole, black holes): star glyph, colored
    // per kind, same edge-of-screen placement as the enemy arrows.
    for (let i = 0; i < 3; i++) {
      const star = document.createElement('div');
      star.textContent = '✦';
      star.style.position = 'absolute';
      star.style.display = 'none';
      star.style.fontSize = '16px';
      this.specialArrows.push(star);
      this.radarEl.appendChild(star);
    }
    this.root.appendChild(this.radarEl);

    // Wave banner ("WAVE n", boss warning, wave clear): one reused div
    // animated via inline transitions, so no stylesheet changes. Parked in
    // the top-center third, under the HUD strip — never over the ship:
    // mid-combat the screen center belongs to the player.
    this.bannerEl = document.createElement('div');
    this.bannerEl.id = 'la-banner';
    const bs = this.bannerEl.style;
    bs.position = 'fixed';
    bs.left = '50%';
    bs.top = '17%';
    bs.transform = 'translate(-50%, -50%) scale(1)';
    bs.padding = '10px 18px';
    // Solid panel chip behind the text: bare glow text drowned against the
    // swarm (bright sprites printed straight through the letters).
    bs.background = colors.panelBg;
    bs.boxShadow = pixelShadow();
    // 26px floor keeps the banner legible on 390px phones (was 20px — tiny
    // over the swarm); the 40px desktop cap is unchanged.
    bs.fontSize = 'clamp(26px, 4.5vw, 40px)';
    bs.letterSpacing = '6px';
    bs.textShadow = '0 0 12px currentColor, 0 0 32px currentColor';
    bs.opacity = '0';
    bs.pointerEvents = 'none';
    bs.zIndex = '40';
    bs.textAlign = 'center';
    // Boss banners with bravery suffixes run long — wrap inside the viewport
    // instead of overflowing narrow screens (390px phones).
    bs.maxWidth = '92vw';
    bs.whiteSpace = 'normal';
    bs.lineHeight = '1.25';
    this.root.appendChild(this.bannerEl);
  }

  setShowFps(show: boolean): void {
    this.showFps = show;
    this.fpsText.style.display = show ? 'flex' : 'none';
  }

  /** Toggle the wormhole bonus-game indicator (game.ts wires the sim flag). */
  setBonusActive(active: boolean): void {
    this.bonusActive = active;
  }

  /** Toggle the space storm indicator (game.ts wires the event flag). */
  setSpaceStormActive(active: boolean): void {
    this.stormActive = active;
  }

  /** Wire the bravery-call button (game.ts queues the call for the sim). */
  setCallWaveHandler(fn: () => void): void {
    this.onCallWave = fn;
  }

  /** Show/hide the whole HUD layer (loadout screensaver keeps the backdrop
   * UI-free). */
  setVisible(visible: boolean): void {
    this.root.style.display = visible ? '' : 'none';
  }

  /** Wire the supply-drop button (game.ts opens the shop overlay). */
  setShopHandler(fn: () => void): void {
    this.onShop = fn;
  }

  /** Pilot seat: paint the captain's portrait (null clears to a dark cell). */
  setCaptainPortrait(canvas: HTMLCanvasElement | null): void {
    const ctx = this.captainCanvas.getContext('2d');
    if (!ctx) return;
    ctx.imageSmoothingEnabled = false;
    ctx.clearRect(0, 0, 48, 48);
    if (canvas) ctx.drawImage(canvas, 0, 0, 48, 48);
  }

  /** Pilot seat: the captain's FIRST name under the portrait (name-plate).
   * The full "Name 'Callsign' Surname" stays on the tooltip and the loadout
   * card; the plate is too narrow for anything more (no ellipsis trimming —
   * a clipped name reads as a bug). */
  setCaptainName(name: string): void {
    const first = name.split(' ')[0] || name;
    this.captainNameEl.textContent = first;
    this.captainNameEl.title = name;
  }

  /**
   * Host the captain-speech chip INSIDE the pilot-seat row, where the skill
   * boxes sit: while the captain speaks, the boxes fold away and the bubble
   * takes their place right of the portrait — the message and the captain
   * share one row (design v3).
   */
  hostCaptainSpeech(el: HTMLElement): void {
    this.speechEl = el;
    this.captainRow.appendChild(el);
  }

  /** Point the radar's star indicators at active specials (wormhole, exit
   * portal, black holes). */
  setSpecialTargets(targets: { x: number; y: number; kind: 'wormhole' | 'blackhole' | 'exit' }[]): void {
    this.specialTargets = targets;
  }

  /** Pilot seat: refresh the skill pips (GUN/PIL/ENG), gold for earned. */
  setCaptainSkills(pips: Record<SkillId, number>): void {
    for (const id of SKILL_IDS) {
      const earned = pips[id] ?? 0;
      this.captainPips[id].forEach((pip, i) => {
        pip.style.background = i < earned ? '#ffd36b' : 'rgba(120,140,170,0.25)';
      });
    }
  }

  /**
   * Flash the wave banner (wave start / boss warning / wave clear), parked
   * in the top-center third under the HUD strip. A single reused div
   * animated with inline transitions: snap visible at
   * 0.85 scale, ease to 1, hold ~1.6 s, fade out. Reflow restarts the pop on
   * rapid successive banners.
   * Reward hue discipline: wave-start and wave-clear/FLAWLESS banners are
   * GOLD (reward family) — green stays reserved for XP/pickup elements; the
   * boss warning keeps its hot pink (threat, not reward).
   */
  showBanner(text: string, tone: 'wave' | 'boss' | 'clear'): void {
    this.bannerEl.textContent = text;
    this.bannerEl.style.color = tone === 'boss' ? '#ff3d81' : colors.accent;
    this.bannerShownAt = performance.now();
    const s = this.bannerEl.style;
    s.filter = '';
    s.transition = 'none';
    s.opacity = '1';
    s.transform = 'translate(-50%, -50%) scale(0.85)';
    void this.bannerEl.offsetWidth;
    s.transition = 'transform 0.35s cubic-bezier(0.2, 0.9, 0.3, 1.4), opacity 0.45s ease-in 1.6s';
    s.transform = 'translate(-50%, -50%) scale(1)';
    s.opacity = '0';
  }

  update(world: WorldState, dt: number, score?: RunScore): void {
    // Hull + shield chip: hull points while a hull exists, otherwise the
    // shield pool; the shield meter is the chip's 2px underline fill. The
    // chip renders once either pool exists (max_hull / max_shield upgrades);
    // a spent hull dims instead of disappearing, so losing the buffer reads
    // as a state, not as missing chrome.
    const maxHull = Math.max(0, world.player.max_hull);
    const maxShield = world.player.max_shield;
    const showHullChip = maxHull > 0 || maxShield > 0;
    this.hullChip.el.style.display = showHullChip ? 'inline-flex' : 'none';
    if (showHullChip) {
      if (maxHull > 0) {
        const hull = Math.ceil(Math.max(0, Math.min(world.player.hull, maxHull)));
        this.hullText.textContent = `${hull}`;
        this.hullChip.el.style.opacity = hull > 0 ? '1' : '0.55';
      } else {
        this.hullText.textContent = `${Math.ceil(Math.max(0, world.player.shield))}`;
        this.hullChip.el.style.opacity = '1';
      }
      if (maxShield > 0) {
        const shieldPct = Math.max(0, Math.min(1, world.player.shield / maxShield));
        this.hullUnder.style.display = 'block';
        this.hullUnder.style.transform = `scaleX(${shieldPct})`;
      } else {
        this.hullUnder.style.display = 'none';
      }
    }

    // RAM chip: visible while the player is at or above ram speed.
    const playerSpeed = Math.hypot(world.player.vel.x, world.player.vel.y);
    this.ramChip.el.style.display = playerSpeed >= RAM_SPEED_THRESHOLD ? 'inline-flex' : 'none';

    // Captain speaking: only the skill boxes fold away — the portrait and
    // name stay (the bubble sits right of the portrait, so it IS the
    // captain talking). 'grid', never '': clearing inline display would
    // destroy the 6-column grid and stack the boxes vertically.
    const barking = this.speechEl !== null && this.speechEl.style.display !== 'none';
    const gridDisplay = barking ? 'none' : 'grid';
    if (this.skillsGridEl && this.skillsGridEl.style.display !== gridDisplay) {
      this.skillsGridEl.style.display = gridDisplay;
    }

    // HP chip: hearts condensed to ×N (one heart per HP_PER_HEART chunk with
    // any HP left in it); the dim "/M" suffix appears while hearts are
    // missing so max HP stays readable (max-HP upgrades grow M).
    const maxHp = Math.max(0, world.player.max_hp);
    const heartCount = Math.max(1, Math.ceil(maxHp / HP_PER_HEART));
    const hp = Math.max(0, Math.min(world.player.hp, maxHp));
    const filled = Math.min(Math.ceil(hp / HP_PER_HEART), heartCount);
    this.hpText.textContent = `×${filled}`;
    if (filled < heartCount) {
      this.hpTotalText.style.display = 'inline';
      this.hpTotalText.textContent = `/${heartCount}`;
    } else {
      this.hpTotalText.style.display = 'none';
    }

    // Flash chip: live charge count, dimmed while the dash is spent.
    const charges = world.player.dash.charges;
    this.flashText.textContent = `×${charges}`;
    this.flashChip.el.style.opacity = charges > 0 ? '1' : '0.55';

    // XP strip: mote progress from the current tier toward the next
    // threshold — the SAME formula the sim uses (thresholdForLevel), so the
    // strip never caps out: tiers are infinite and the fill is always live.
    const tier = Math.max(world.player.lumensurge_tier, 1);
    const motes = score?.motes ?? 0;
    const lo = thresholdForLevel(tier);
    const hi = thresholdForLevel(tier + 1);
    const span = Math.max(1, hi - lo);
    const surgePct = Math.min(1, Math.max(0, (motes - lo) / span));
    this.xpFill.style.transform = `scaleX(${surgePct})`;
    // Pre-surge glow: ≥90% of the next tier pulses XP green ("level up soon").
    if (surgePct >= 0.9) {
      const pulse = 0.6 + 0.4 * Math.sin(world.tick * 0.35);
      this.xpFill.style.boxShadow = `0 0 ${(4 + 5 * pulse).toFixed(1)}px rgba(125, 255, 79, ${pulse.toFixed(2)})`;
    } else {
      this.xpFill.style.boxShadow = '';
    }
    // The star badge at the strip's right end carries the tier (the old
    // multiplier read); no mote counter — the strip is the progress.
    this.xpLevelText.textContent = `×${tier}`;

    // Serenity chip: the meter only exists while the ship holds still (or a
    // charge is already banked) — hidden outright while flying. Underline
    // reads ice → gold at tier 3.
    const serenity = getSerenityMods(world);
    this.serenityChip.el.style.display =
      playerSpeed < 0.5 || serenity.value > 0 ? 'inline-flex' : 'none';
    this.serenityUnder.style.transform = `scaleX(${serenity.value.toFixed(3)})`;
    this.serenityUnder.style.background = serenity.tier >= 3 ? '#ffd36b' : '#9fefff';
    if (serenity.burning) {
      // Burn state (capped at tier 1 until relocation): the chip dims and the
      // label says what to do — "doesn't grow" is a rule, not a bug.
      this.serenityChip.el.style.opacity = '0.55';
      this.serenityText.textContent = '→';
      this.serenityText.style.color = '#9fefff';
      this.serenityText.title = 'Move to recharge';
    } else {
      this.serenityChip.el.style.opacity = '1';
      // Tier numeral (III = full "anchor" charge; the ⚓ glyph was dropped
      // with the zero-emoji top-HUD rule).
      this.serenityText.textContent =
        serenity.tier >= 3 ? 'III' : serenity.tier >= 2 ? 'II' : serenity.tier >= 1 ? 'I' : '';
      this.serenityText.style.color = serenity.tier >= 3 ? '#ffd36b' : '';
      this.serenityText.title = '';
    }

    this.enemyChipText.textContent = `${world.enemies.aliveCount}`;
    this.scoreChipText.textContent = (score?.total ?? 0).toLocaleString('en-US');
    this.killChipText.textContent = `${score?.kills ?? 0}`;
    this.killText.textContent = `${score?.kills ?? 0}`;

    // Mobile stats strip mirrors (bolt ×tier · bug enemies · trophy score ·
    // skull kills) — the strip is hidden on desktop, so updates are cheap.
    if (this.stripMultText) this.stripMultText.textContent = `×${world.player.lumensurge_tier}`;
    if (this.stripEnemyText) this.stripEnemyText.textContent = `${world.enemies.aliveCount}`;
    if (this.stripScoreText) this.stripScoreText.textContent = (score?.total ?? 0).toLocaleString('en-US');
    if (this.stripKillText) this.stripKillText.textContent = `${score?.kills ?? 0}`;

    // Radar: the field reads empty but the arena isn't — point at the nearest
    // off-view enemies. Bosses always rate an arrow (hot pink) even with
    // minions on screen; trash only when the view is truly empty.
    {
      const player = world.player;
      const viewR = world.viewRadius * 1.05;
      let visibleCount = 0;
      const bosses: { angle: number; dist: number }[] = [];
      const nearest: { angle: number; dist: number }[] = [];
      for (const e of world.enemies.alive()) {
        if (!e.alive) continue;
        const d = wrappedDelta(e.pos, player.pos);
        const dist = Math.hypot(d.x, d.y);
        if (dist <= viewR) {
          visibleCount++;
        } else if (e.boss) {
          bosses.push({ angle: Math.atan2(d.y, d.x), dist });
        } else {
          nearest.push({ angle: Math.atan2(d.y, d.x), dist });
        }
      }
      const showTrash = world.enemies.aliveCount > 0 && visibleCount === 0;
      bosses.sort((a, b) => a.dist - b.dist);
      nearest.sort((a, b) => a.dist - b.dist);
      const targets: { angle: number; boss: boolean }[] = [];
      if (!world.gameOver) {
        for (const b of bosses) targets.push({ angle: b.angle, boss: true });
        if (showTrash) for (const t of nearest) targets.push({ angle: t.angle, boss: false });
      }
      for (let i = 0; i < this.radarArrows.length; i++) {
        const arrow = this.radarArrows[i]!;
        const target = targets[i];
        if (!target) {
          arrow.style.display = 'none';
          continue;
        }
        const astyle = arrow.style as CSSStyleDeclaration & { webkitTextStroke?: string };
        astyle.display = 'block';
        // Boss arrows read as a distinct threat color; trash stays hollow ice.
        astyle.color = target.boss ? '#ff3d81' : 'transparent';
        astyle.webkitTextStroke = target.boss ? '0px' : '1px #9fefff';
        astyle.opacity = (0.55 + 0.45 * Math.sin(world.tick * 0.22 + i * 1.7)).toFixed(2);
        astyle.left = `${50 + Math.cos(target.angle) * 42}%`;
        astyle.top = `${50 + Math.sin(target.angle) * 42}%`;
        astyle.transform = `translate(-50%, -50%) rotate(${(target.angle + Math.PI / 2).toFixed(3)}rad)`;
      }

      // Special-object stars: wormhole (bonus purple), subspace exit portal
      // (escape green) and black holes (void violet) at the screen edge,
      // shown only while they're off-view.
      for (let i = 0; i < this.specialArrows.length; i++) {
        const star = this.specialArrows[i]!;
        const target = this.specialTargets[i];
        if (world.gameOver || !target) {
          star.style.display = 'none';
          continue;
        }
        const d = wrappedDelta({ x: target.x, y: target.y }, world.player.pos);
        const dist = Math.hypot(d.x, d.y);
        if (dist <= viewR) {
          star.style.display = 'none';
          continue;
        }
        const angle = Math.atan2(d.y, d.x);
        const color =
          target.kind === 'wormhole' ? '#c86bff' : target.kind === 'exit' ? '#4dffb8' : '#8a6bff';
        star.style.display = 'block';
        star.style.color = color;
        star.style.textShadow = `0 0 8px ${color}`;
        star.style.opacity = (0.6 + 0.4 * Math.sin(world.tick * 0.3 + i * 2.1)).toFixed(2);
        star.style.left = `${50 + Math.cos(angle) * 42}%`;
        star.style.top = `${50 + Math.sin(angle) * 42}%`;
        star.style.transform = `translate(-50%, -50%) rotate(${(angle + Math.PI / 2).toFixed(3)}rad)`;
      }
    }

    const totalSeconds = Math.floor(world.tick / 60);
    const mm = Math.floor(totalSeconds / 60).toString().padStart(2, '0');
    const ss = (totalSeconds % 60).toString().padStart(2, '0');
    this.timerText.textContent = `${mm}:${ss}`;

    // Wave chip: Wn / Bn on boss waves, dimmed while the next wave is
    // incoming. (A phase-progress underline here read as a selection state
    // in the A/B review — the XP strip owns progress communication.)
    const wave = getWaveInfo(world);
    this.waveChipText.textContent = wave.boss ? `B${wave.displayWave}` : `W${wave.displayWave}`;
    this.waveChip.el.style.opacity = wave.phase === 'intermission' ? '0.6' : '1';

    // Bravery call: visible only while the intermission can be skipped; the
    // label counts down with the shrinking bonus. Hidden during a wormhole
    // subspace trip (the suspended wave clock can sit on a callable
    // intermission the whole time).
    if (wave.callBonus > 0 && world.bonus.phase === 'idle') {
      this.callWaveBtn.style.display = 'inline-flex';
      this.callWaveLabel.textContent = `CALL +${wave.callBonus}`;
    } else {
      this.callWaveBtn.style.display = 'none';
    }

    // Supply drop: visible only during intermission while the run is live.
    // Hidden during a wormhole subspace trip (its suspended wave clock can
    // read as an intermission, but a mid-trip shop pause would cheapen it).
    const showShop = wave.phase === 'intermission' && !world.gameOver && world.bonus.phase === 'idle';
    this.shopBtn.style.display = showShop ? 'inline-flex' : 'none';

    // BONUS chip: deterministic pulse off the sim tick while active. During a
    // subspace trip the label carries the live status — banked score and
    // objective progress — instead of the plain WORMHOLE tag.
    this.bonusChip.el.style.display = this.bonusActive ? 'inline-flex' : 'none';
    if (this.bonusActive) {
      this.bonusChip.el.style.opacity = (0.7 + 0.3 * Math.sin(world.tick * 0.12)).toFixed(3);
      const b = world.bonus;
      if (b.phase === 'entering' || b.phase === 'fighting' || b.phase === 'boon') {
        this.bonusChip.text.textContent = `SUB +${b.subspace_score} · ${b.kills}/${b.kill_target}`;
      } else if (b.phase === 'exiting') {
        this.bonusChip.text.textContent = `SUB +${b.subspace_score} · OUT`;
      } else {
        this.bonusChip.text.textContent = 'WORMHOLE';
      }
    }

    // SPACE STORM chip: same deterministic pulse pattern as BONUS.
    this.stormChip.el.style.display = this.stormActive ? 'inline-flex' : 'none';
    if (this.stormActive) {
      this.stormChip.el.style.opacity = (0.7 + 0.3 * Math.sin(world.tick * 0.12)).toFixed(3);
    }

    this.updateBannerOcclusion(world);

    if (this.showFps) {
      this.frameCount++;
      this.fpsTimer += dt;
      if (this.fpsTimer >= 0.5) {
        this.lastFps = Math.round(this.frameCount / this.fpsTimer);
        this.frameCount = 0;
        this.fpsTimer = 0;
      }
      this.fpsText.textContent = `FPS ${this.lastFps}`;
    }
  }

  dispose(): void {
    this.root.remove();
  }

  /**
   * Banner occlusion rule: the banner plate is opaque, so while one is live
   * any enemy/pickup crossing its screen rect dims it to a ghost (18%) —
   * the world reads through the plate instead of hiding under it. Projection
   * mirrors the renderer: camera centered on the player, the smaller
   * viewport dimension spanning 2×viewRadius (camera lead/kick are small
   * enough to ignore for a dim, not a cull). Cheap: one rect read plus an
   * O(n) scan, and only during the ~2 s a banner is up.
   */
  private updateBannerOcclusion(world: WorldState): void {
    if (performance.now() - this.bannerShownAt > 2100) {
      if (this.bannerEl.style.filter) this.bannerEl.style.filter = '';
      return;
    }
    const rect = this.bannerEl.getBoundingClientRect();
    const cx = window.innerWidth / 2;
    const cy = window.innerHeight / 2;
    const pxPerUnit = Math.min(cx * 2, cy * 2) / (2 * Math.max(world.viewRadius, 1e-6));
    const inside = (pos: Vec2): boolean => {
      const d = wrappedDelta(pos, world.player.pos);
      const sx = cx + d.x * pxPerUnit;
      const sy = cy - d.y * pxPerUnit;
      return sx >= rect.left && sx <= rect.right && sy >= rect.top && sy <= rect.bottom;
    };
    let occluded = false;
    for (const e of world.enemies.alive()) {
      if (e.alive && inside(e.pos)) {
        occluded = true;
        break;
      }
    }
    if (!occluded) {
      for (const p of world.pickups.alive()) {
        if (p.alive && inside(p.pos)) {
          occluded = true;
          break;
        }
      }
    }
    const filter = occluded ? 'opacity(0.18)' : '';
    if (this.bannerEl.style.filter !== filter) this.bannerEl.style.filter = filter;
  }

  private makePanel(parent: HTMLElement, posClass: string): HTMLElement {
    const el = document.createElement('div');
    el.className = `la-panel ${posClass}`;
    parent.appendChild(el);
    return el;
  }

  private makeRow(): HTMLElement {
    const el = document.createElement('div');
    el.className = 'la-row';
    return el;
  }

  /** One 24px rail chip: framed box with an optional leading glyph + text. */
  private makeChip(glyph: HTMLElement | null): HudChip {
    const el = document.createElement('span');
    el.className = 'la-chip';
    if (glyph) el.appendChild(glyph);
    const text = document.createElement('span');
    el.appendChild(text);
    return { el, text };
  }

  /** 2px underline fill inside a chip — the continuous-meter read. */
  private makeUnderline(chip: HTMLElement): HTMLElement {
    const under = document.createElement('span');
    under.className = 'la-chip-under';
    chip.appendChild(under);
    return under;
  }

  /** Pixel-framed rail action button (CALL / SHOP), hidden until shown. Chip
   * height (24px), not the menu 44px: the rail must stay inside the 72px
   * mobile top-HUD budget even with both buttons up. Filled GOLD with dark
   * text — the inverse of the read-only chips (navy fill, light text) — so
   * the two CTAs read as tappable at a glance; .la-rail-btn adds the :active
   * press sink (inline styles can't). */
  private makeWaveButton(glyph: string): HTMLButtonElement {
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'la-rail-btn';
    const cb = btn.style;
    cb.display = 'none';
    cb.alignItems = 'center';
    cb.gap = '4px';
    cb.height = '24px';
    cb.minHeight = '24px';
    cb.boxSizing = 'border-box';
    cb.padding = '0 8px';
    cb.margin = '0';
    cb.border = '0';
    cb.borderRadius = '0';
    cb.background = colors.accent;
    cb.color = colors.barTrack;
    cb.boxShadow = pixelShadow('#8f6b1e', 2);
    cb.fontFamily = 'inherit';
    cb.fontSize = 'inherit';
    cb.letterSpacing = '1px';
    cb.cursor = 'pointer';
    cb.pointerEvents = 'auto';
    cb.touchAction = 'manipulation';
    cb.flex = 'none';
    btn.appendChild(icon(glyph, 12));
    return btn;
  }
}
